// การเรียกข้อมูลของหน้า Overview — ทุกตัวไปที่ relay ของเราเอง (/app-api/v1/...)
//
// ทุกอันต้องมี token Wazzup ของผู้ใช้ ไม่มี service key (docs/SYSTEM-INTEGRATION-GUIDELINE.md)
import type { Brand } from "@contracts/api";
import { inboxAuthHeaders, isLinePage } from "@features/inbox/api/inbox-api";
import { api } from "@shared/api/client";
import type {
  BiddableAccount,
  BiddableMetrics,
  InboxAnalytics,
  InboxConversation,
  InboxPost,
  KolCampaignSummary,
  KolPost,
} from "./upstream-types";

/** เพจ Facebook ของแบรนด์ (LINE ไม่มีข้อมูลพวกนี้ให้ดึง) */
export const fbPageIds = (brand: Brand) => (brand.pageIds ?? []).filter((id) => !isLinePage(id));

function authOrThrow() {
  const headers = inboxAuthHeaders();
  if (!headers) throw new Error("ยังไม่ได้เข้าสู่ระบบ Wazzup");
  return headers;
}

/** ชดเชย timezone ให้ปลายทางตัดวันตรงกับเครื่องผู้ใช้ (นาที, ฝั่งบวกคือ UTC+) */
const tzMinutes = () => String(-new Date().getTimezoneOffset());

export const overviewKeys = {
  inboxAnalytics: (ids: string[], days: number) => ["ov", "inbox", ids.join(","), days] as const,
  conversations: (ids: string[]) => ["ov", "rooms", ids.join(",")] as const,
  content: (ids: string[], days: number) => ["ov", "content", ids.join(","), days] as const,
  kolSummary: (refs: string[], days: number) => ["ov", "kol", refs.join(","), days] as const,
  kolPosts: (refs: string[], days: number) => ["ov", "kolposts", refs.join(","), days] as const,
  ads: (preset: string) => ["ov", "ads", preset] as const,
};

export async function fetchInboxAnalytics(ids: string[], days: number): Promise<InboxAnalytics> {
  const to = new Date();
  const from = new Date(to.getTime() - (days - 1) * 86400000);
  const day = (d: Date) => d.toISOString().slice(0, 10);
  const { data } = await api.get<InboxAnalytics>("/inbox/analytics", {
    headers: authOrThrow(),
    params: { pageId: ids.join(","), from: day(from), to: day(to), tz: tzMinutes() },
  });
  return data;
}

export async function fetchConversations(ids: string[], limit = 10): Promise<InboxConversation[]> {
  const { data } = await api.get<{ items?: InboxConversation[] }>("/inbox/conversations", {
    headers: authOrThrow(),
    params: { pageId: ids.join(","), limit, tz: tzMinutes() },
  });
  return data?.items ?? [];
}

export type ContentPost = InboxPost & { pageName?: string };

/** โพสต์ของทุกเพจในช่วงวันที่เลือก — ยิงแยกต่อเพจเพราะปลายทางรับทีละเพจ */
export async function fetchContentPosts(ids: string[], days: number): Promise<ContentPost[]> {
  const headers = authOrThrow();
  const results = await Promise.all(
    ids.map(async (id) => {
      try {
        const { data } = await api.get<{ posts?: InboxPost[]; pageName?: string }>("/inbox/posts", {
          headers,
          params: { pageId: id, days },
        });
        return (data?.posts ?? []).map((p) => ({ ...p, pageName: data?.pageName }));
      } catch {
        // เพจเดียวพังไม่ควรทำให้ทั้งบล็อกว่าง — ข้ามเพจนั้นไป
        return [];
      }
    }),
  );
  return results.flat();
}

/** สรุป KOL ต่อรายงาน — คืน Map ให้ค้นด้วย refId ได้ตรง ๆ */
export async function fetchKolSummary(
  refs: string[],
  days: number,
): Promise<Map<string, KolCampaignSummary>> {
  const { data } = await api.get<{ campaigns?: KolCampaignSummary[] }>("/kol/summary", {
    headers: authOrThrow(),
    params: { keys: refs.join(","), ...(days ? { days } : {}) },
  });
  return new Map((data?.campaigns ?? []).map((c) => [c.key, c]));
}

export type KolPostWithRef = KolPost & { ref: string };

/** ผูกหลายรายงาน = ดึงทุกตัวแล้วรวมกัน เรียงตามวิวอีกรอบให้ตัวเด่นขึ้นก่อน */
export async function fetchKolPosts(refs: string[], days: number): Promise<KolPostWithRef[]> {
  const headers = authOrThrow();
  const results = await Promise.all(
    refs.map(async (ref) => {
      try {
        const { data } = await api.get<{ posts?: KolPost[] }>("/kol/posts", {
          headers,
          params: { campaign: ref, limit: 12, ...(days ? { days } : {}) },
        });
        return (data?.posts ?? []).map((p) => ({ ...p, ref }));
      } catch {
        return [];
      }
    }),
  );
  return results.flat().sort((a, b) => (b.views ?? 0) - (a.views ?? 0));
}

export async function fetchBiddableScope(preset: string): Promise<BiddableAccount[]> {
  const { data } = await api.get<{ accounts?: BiddableAccount[] }>("/biddable/scope", {
    headers: authOrThrow(),
    params: preset ? { datePreset: preset } : undefined,
    // Biddable ต้องไปถาม Windsor ซึ่งครั้งแรกของวันช้าเป็นนาที
    timeout: 180_000,
  });
  return Array.isArray(data?.accounts) ? data.accounts : [];
}

/** ตัวเลขของ scope ที่ผูกไว้: ระบุแคมเปญ = เอาของแคมเปญนั้น ไม่ระบุ = ยอดรวมของบัญชี */
export function biddableMetrics(
  accounts: BiddableAccount[] | undefined,
  meta: Record<string, unknown> | null,
): BiddableMetrics | null {
  const account = meta?.account as string | undefined;
  if (!accounts || !account) return null;
  const acc = accounts.find((a) => a.name === account);
  if (!acc) return null;
  const campaign = meta?.campaign as string | undefined;
  if (!campaign) return acc;
  return acc.campaigns?.find((c) => c.name === campaign) ?? null;
}
